export interface Stick { x: number; y: number; active: boolean; source: 'pointer' | 'gamepad' | 'keyboard' | 'none'; }

const RADIUS = 80;
const GAMEPAD_DEADZONE = 0.15;
/** Keyboard "stick" ramp: seconds to full deflection on press / back to centre on release.
 *  Keys are digital, so a short attack/decay makes flight steerable instead of twitchy. */
const KEY_ATTACK = 0.18;
const KEY_RELEASE = 0.10;

export class Input {
  private anchor = { x: 0, y: 0 };
  private current = { x: 0, y: 0 };
  private active = false;
  private ringEl: HTMLDivElement | null = null;
  private dotEl: HTMLDivElement | null = null;
  private gamepadConnected = false;
  private gamepadHintEl: HTMLDivElement | null = null;

  constructor(private canvas: HTMLCanvasElement) {
    canvas.addEventListener('pointerdown', this.onDown);
    canvas.addEventListener('pointermove', this.onMove);
    canvas.addEventListener('pointerup', this.onUp);
    canvas.addEventListener('pointercancel', this.onUp);
    // Only treat 'leave' as release on touch — on desktop, dragging beyond the window
    // edge during a fast maneuver shouldn't break the stick (pointer capture keeps moves coming).
    canvas.addEventListener('pointerleave', this.onLeave);

    window.addEventListener('gamepadconnected', this.onGamepadConnect);
    window.addEventListener('gamepaddisconnected', this.onGamepadDisconnect);

    // Keyboard — full desktop scheme (WASD/arrows steer, Space fire, Shift boost, M/Tab map,
    // Q/E jump unit, F crisis-jump). Tracked as a key-set; getters below synthesize a stick.
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('blur', () => this.keys.clear()); // alt-tab mustn't leave keys stuck
    // Some browsers don't fire 'gamepadconnected' until the user presses a button.
    // We poll each frame in getStick() regardless.
    this.ringEl = document.createElement('div');
    this.ringEl.style.cssText = [
      'position:fixed',
      'width:160px', 'height:160px',
      'border:2px solid rgba(255,255,255,0.55)',
      'border-radius:50%',
      'pointer-events:none',
      'z-index:5',
      'transform:translate(-50%,-50%)',
      'display:none',
      'box-shadow:0 0 20px rgba(0,0,0,0.35)',
    ].join(';');
    document.body.appendChild(this.ringEl);
    this.dotEl = document.createElement('div');
    this.dotEl.style.cssText = [
      'position:fixed',
      'width:18px', 'height:18px',
      'background:rgba(255,255,255,0.85)',
      'border-radius:50%',
      'pointer-events:none',
      'z-index:6',
      'transform:translate(-50%,-50%)',
      'display:none',
    ].join(';');
    document.body.appendChild(this.dotEl);

    this.gamepadHintEl = document.createElement('div');
    this.gamepadHintEl.textContent = '🎮 GAMEPAD CONNECTED';
    this.gamepadHintEl.style.cssText = [
      'position:fixed',
      'left:10px', 'bottom:10px',
      'padding:4px 8px',
      'background:rgba(0,0,0,0.55)',
      'color:#fff',
      'font:12px ui-monospace,monospace',
      'letter-spacing:1px',
      'border-radius:4px',
      'pointer-events:none',
      'z-index:7',
      'display:none',
      'opacity:0',
      'transition:opacity 0.3s',
    ].join(';');
    document.body.appendChild(this.gamepadHintEl);
  }

  // ---------------- keyboard ----------------
  private keys = new Set<string>();
  /** True once any flight/fire key has been pressed — used to pick desktop hint text. */
  keyboardSeen = false;
  /** Smoothed keyboard stick (ramped: see KEY_ATTACK/KEY_RELEASE). */
  private kb = { x: 0, y: 0 };
  private kbLastT = 0;

  private onKeyDown = (e: KeyboardEvent) => {
    // Don't fight the browser for Tab focus-cycling or trigger page scroll on Space/arrows.
    if (['Space', 'Tab', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.code)) e.preventDefault();
    if (e.repeat) return;
    this.keys.add(e.code);
    this.keyboardSeen = true;
  };
  private onKeyUp = (e: KeyboardEvent) => { this.keys.delete(e.code); };

  private keyHeld(...codes: string[]): boolean { return codes.some((c) => this.keys.has(c)); }

  /** Advance the keyboard-stick ramp toward the currently-held direction (wall-time stepped,
   *  safe to call several times per frame — repeat calls in the same frame see ~0 delta). */
  private keyboardStick(): { x: number; y: number } | null {
    const tx = (this.keyHeld('KeyD', 'ArrowRight') ? 1 : 0) + (this.keyHeld('KeyA', 'ArrowLeft') ? -1 : 0);
    // Arcade convention matching touch drag: W/Up = climb (stick y negative), S/Down = dive.
    const ty = (this.keyHeld('KeyS', 'ArrowDown') ? 1 : 0) + (this.keyHeld('KeyW', 'ArrowUp') ? -1 : 0);
    const now = performance.now() / 1000;
    const dt = Math.min(0.05, Math.max(0, now - this.kbLastT));
    this.kbLastT = now;
    const step = (cur: number, target: number) => {
      const rate = target !== 0 ? KEY_ATTACK : KEY_RELEASE;
      const k = 1 - Math.exp(-dt / (rate / 3));
      return cur + (target - cur) * k;
    };
    this.kb.x = step(this.kb.x, tx);
    this.kb.y = step(this.kb.y, ty);
    if (Math.abs(this.kb.x) < 0.02 && Math.abs(this.kb.y) < 0.02 && tx === 0 && ty === 0) return null;
    return { x: this.kb.x, y: this.kb.y };
  }

  /** Space held = guns. */
  keyFireHeld(): boolean { return this.keyHeld('Space'); }
  /** Shift held = afterburner (digital full pull). */
  keyBoostHeld(): boolean { return this.keyHeld('ShiftLeft', 'ShiftRight'); }

  /** Keyboard edge detector (mirrors pressedEdge): true the frame any of `codes` goes down.
   *  Like pressedEdge, poll a given action at most once per frame. */
  private _keyPrev: Record<string, boolean> = {};
  keyPressedEdge(action: string, ...codes: string[]): boolean {
    const down = this.keyHeld(...codes);
    const edge = down && !this._keyPrev[action];
    this._keyPrev[action] = down;
    return edge;
  }

  private onGamepadConnect = (_: GamepadEvent) => {
    this.gamepadConnected = true;
    if (this.gamepadHintEl) {
      this.gamepadHintEl.style.display = 'block';
      requestAnimationFrame(() => { if (this.gamepadHintEl) this.gamepadHintEl.style.opacity = '1'; });
      setTimeout(() => {
        if (this.gamepadHintEl) {
          this.gamepadHintEl.style.opacity = '0';
          setTimeout(() => { if (this.gamepadHintEl) this.gamepadHintEl.style.display = 'none'; }, 400);
        }
      }, 2500);
    }
  };

  private onGamepadDisconnect = () => {
    // don't clear gamepadConnected — getStick polls fresh each frame
  };

  private pollGamepad(): { x: number; y: number } | null {
    const gps = navigator.getGamepads?.() ?? [];
    for (const gp of gps) {
      if (!gp || !gp.connected) continue;
      if (!this.gamepadConnected) this.gamepadConnected = true;
      // Prefer left stick (axes 0,1); fall back to right stick (2,3) if left is idle.
      let x = gp.axes[0] ?? 0;
      let y = gp.axes[1] ?? 0;
      if (Math.hypot(x, y) < GAMEPAD_DEADZONE) {
        const rx = gp.axes[2] ?? 0;
        const ry = gp.axes[3] ?? 0;
        if (Math.hypot(rx, ry) >= GAMEPAD_DEADZONE) { x = rx; y = ry; }
        else return null;
      }
      // Deadzone shaping: remap so values just past the deadzone don't feel dead.
      const mag = Math.hypot(x, y);
      if (mag < GAMEPAD_DEADZONE) return null;
      const scaled = (mag - GAMEPAD_DEADZONE) / (1 - GAMEPAD_DEADZONE);
      const nx = (x / mag) * Math.min(1, scaled);
      const ny = (y / mag) * Math.min(1, scaled);
      return { x: nx, y: ny };
    }
    return null;
  }

  private onDown = (e: PointerEvent) => {
    this.canvas.setPointerCapture(e.pointerId);
    this.anchor = { x: e.clientX, y: e.clientY };
    this.current = { ...this.anchor };
    this.active = true;
    if (this.ringEl) {
      this.ringEl.style.left = `${this.anchor.x}px`;
      this.ringEl.style.top = `${this.anchor.y}px`;
      this.ringEl.style.display = 'block';
    }
    if (this.dotEl) {
      this.dotEl.style.left = `${this.anchor.x}px`;
      this.dotEl.style.top = `${this.anchor.y}px`;
      this.dotEl.style.display = 'block';
    }
    e.preventDefault();
  };

  private onMove = (e: PointerEvent) => {
    if (!this.active) return;
    this.current = { x: e.clientX, y: e.clientY };
    if (this.dotEl) {
      const s = this.getStick();
      const dx = s.x * RADIUS;
      const dy = s.y * RADIUS;
      this.dotEl.style.left = `${this.anchor.x + dx}px`;
      this.dotEl.style.top = `${this.anchor.y + dy}px`;
    }
  };

  private onUp = () => {
    this.active = false;
    if (this.ringEl) this.ringEl.style.display = 'none';
    if (this.dotEl) this.dotEl.style.display = 'none';
  };

  private onLeave = (e: PointerEvent) => {
    if (e.pointerType === 'touch') this.onUp();
  };

  getStick(): Stick {
    // Pointer drag wins while active (user grabbed the virtual stick).
    if (this.active) {
      let dx = this.current.x - this.anchor.x;
      let dy = this.current.y - this.anchor.y;
      const len = Math.hypot(dx, dy);
      if (len > RADIUS) {
        dx = (dx / len) * RADIUS;
        dy = (dy / len) * RADIUS;
      }
      return { x: dx / RADIUS, y: dy / RADIUS, active: true, source: 'pointer' };
    }
    // Otherwise, try the gamepad.
    const gp = this.pollGamepad();
    if (gp) {
      // Flight-sim convention on gamepad: pulling the stick BACK (down on the thumbstick)
      // makes the plane climb. The pointer drag stays screen-natural (drag down = dive);
      // we only invert Y for the gamepad.
      return { x: gp.x, y: -gp.y, active: true, source: 'gamepad' };
    }
    // Finally the keyboard (WASD/arrows), already in touch convention (up = climb).
    const kb = this.keyboardStick();
    if (kb) return { x: kb.x, y: kb.y, active: true, source: 'keyboard' };
    return { x: 0, y: 0, active: false, source: 'none' };
  }

  /** Left thumbstick only — pointer drag wins, else gamepad axes 0,1 (no right-stick
   *  fallback, unlike getStick()). For the tank where right stick is a separate control. */
  getLeftStick(): Stick {
    if (this.active) return this.getStick(); // pointer drag
    const gps = navigator.getGamepads?.() ?? [];
    for (const gp of gps) {
      if (!gp || !gp.connected) continue;
      const lx = gp.axes[0] ?? 0;
      const ly = gp.axes[1] ?? 0;
      const mag = Math.hypot(lx, ly);
      if (mag < GAMEPAD_DEADZONE) return { x: 0, y: 0, active: false, source: 'gamepad' };
      const scaled = (mag - GAMEPAD_DEADZONE) / (1 - GAMEPAD_DEADZONE);
      const nx = (lx / mag) * Math.min(1, scaled);
      const ny = (ly / mag) * Math.min(1, scaled);
      return { x: nx, y: -ny, active: true, source: 'gamepad' };
    }
    const kb = this.keyboardStick();
    if (kb) return { x: kb.x, y: kb.y, active: true, source: 'keyboard' };
    return { x: 0, y: 0, active: false, source: 'none' };
  }

  /** Right thumbstick only (gamepad axes 2,3), deadzoned + Y-inverted to match getStick().
   *  Used for the tank: left stick drives, right stick traverses the turret. Returns an
   *  inactive stick when there's no gamepad or the stick is centred. */
  getRightStick(): Stick {
    const gps = navigator.getGamepads?.() ?? [];
    for (const gp of gps) {
      if (!gp || !gp.connected) continue;
      const rx = gp.axes[2] ?? 0;
      const ry = gp.axes[3] ?? 0;
      const mag = Math.hypot(rx, ry);
      if (mag < GAMEPAD_DEADZONE) return { x: 0, y: 0, active: false, source: 'gamepad' };
      const scaled = (mag - GAMEPAD_DEADZONE) / (1 - GAMEPAD_DEADZONE);
      const nx = (rx / mag) * Math.min(1, scaled);
      const ny = (ry / mag) * Math.min(1, scaled);
      return { x: nx, y: -ny, active: true, source: 'gamepad' };
    }
    return { x: 0, y: 0, active: false, source: 'none' };
  }

  /** True if a gamepad button was tapped since last call (used for splash/game-over dismiss). */
  consumeGamepadStart(): boolean {
    const gps = navigator.getGamepads?.() ?? [];
    for (const gp of gps) {
      if (!gp || !gp.connected) continue;
      // Any face/start button counts as "begin".
      const pressed = (gp.buttons[0]?.pressed) || (gp.buttons[9]?.pressed) || (gp.buttons[7]?.pressed);
      if (pressed) return true;
    }
    return false;
  }

  /** Running-minimum rest value seen per trigger AXIS, used to self-calibrate.
   *  The Gamepad API reports an axis-based trigger as 0 until it's first touched (NOT its true -1
   *  rest), which otherwise reads as a phantom half-pull at startup. Tracking the lowest value ever
   *  seen lets us map [rest..+1] → [0..1]: at startup rest=0 so the trigger reads neutral, and once
   *  it's been released the rest settles to -1 and the full travel works. */
  private _trigRest: Record<number, number> = {};

  /** Analog trigger pull in 0..1 from EITHER the standard button (`btnIndex`, e.g. Xbox triggers on
   *  buttons 6/7) OR the trigger AXIS (`axisIndex`). Many "standard" pads expose LT/RT only on axes
   *  4/5 (rest -1 → +1, buttons 6/7 dead); the axis path self-calibrates its rest (see _trigRest)
   *  so a fresh-start trigger reads neutral, not half-pulled. */
  private triggerAmount(btnIndex: number, axisIndex: number): number {
    const gps = navigator.getGamepads?.() ?? [];
    let amt = 0;
    for (const gp of gps) {
      if (!gp || !gp.connected) continue;
      amt = Math.max(amt, gp.buttons[btnIndex]?.value ?? 0); // buttons rest at 0 — always trustworthy
      if (gp.axes.length > axisIndex) {
        const raw = gp.axes[axisIndex] ?? -1;
        const rest = this._trigRest[axisIndex] = Math.min(this._trigRest[axisIndex] ?? raw, raw);
        const span = 1 - rest;
        if (span > 0.1) amt = Math.max(amt, (raw - rest) / span);
      }
    }
    return amt < 0.06 ? 0 : Math.min(1, amt);
  }

  /** LT pull amount (0..1) — drives the ANALOG afterburner. */
  gamepadBoostAmount(): number {
    return this.triggerAmount(6, 4);
  }

  /** True while RT (right trigger) is pulled past a small deadzone — manual gun fire. Fire is a
   *  digital action, so the threshold is low: any clear pull shoots reliably (a high threshold made
   *  moderate holds flicker on/off → intermittent fire + gun sound). */
  gamepadFireHeld(): boolean {
    return this.triggerAmount(7, 5) > 0.12;
  }

  /** Generic edge detector: true on the frame `index` goes released→pressed (any pad).
   *  Standard mapping: 0 A, 1 B, 2 X, 3 Y, 4 LB, 5 RB, 6 LT, 7 RT, 8 Back, 9 Start,
   *  10 L3, 11 R3, 12-15 D-pad U/D/L/R. Each index must be polled at most once per frame
   *  (a second call the same frame sees no edge), so the loop routes by context. */
  private _btn: Record<number, boolean> = {};
  pressedEdge(index: number): boolean {
    const gps = navigator.getGamepads?.() ?? [];
    let pressed = false;
    for (const gp of gps) {
      if (!gp || !gp.connected) continue;
      if (gp.buttons[index]?.pressed) pressed = true;
    }
    const edge = pressed && !this._btn[index];
    this._btn[index] = pressed;
    return edge;
  }
}
